import { creatorbase } from '@round/api';
import { numberWithCommas } from 'helpers';
import { isNumber } from 'utility/utility';
import { formatDecimalToPercentageString } from '@round/utils';

export const formatPostStatsCellValue = (value: number | null | undefined) => {
    if (typeof value !== 'number') {
        return '-';
    }

    return numberWithCommas(value);
};

export const getTikTokTotalEngagement = (post: creatorbase.PublicTiktokPost) => {
    if (
        !post.tiktok_details ||
        (!isNumber(post.tiktok_details.like_count) &&
            !isNumber(post.tiktok_details.comment_count) &&
            !isNumber(post.tiktok_details.share_count) &&
            !isNumber(post.tiktok_details.save_count))
    ) {
        return null;
    }

    const { like_count, comment_count, share_count, save_count } = post.tiktok_details;
    return (like_count ?? 0) + (comment_count ?? 0) + (share_count ?? 0) + (save_count ?? 0);
};

export const getTiktokEngagementRate = (post: creatorbase.PublicTiktokPost) => {
    const engagements = getTikTokTotalEngagement(post);
    if (!isNumber(post.view_count) || !isNumber(engagements)) {
        return null;
    }
    if (post.view_count === 0 || engagements === 0) {
        return 0;
    }
    return engagements / post.view_count;
};

export const getInstagramTotalEngagement = (
    post: creatorbase.PublicInstagramPost['instagram_details'] | null | undefined
) => {
    if (!post || (!isNumber(post.like_count) && !isNumber(post.comment_count))) {
        return null;
    }

    const { like_count, comment_count } = post;
    return (like_count ?? 0) + (comment_count ?? 0);
};

export const getInstagramEngagementRate = (post: creatorbase.PublicInstagramPost) => {
    const engagements = getInstagramTotalEngagement(post.instagram_details);

    if ((!isNumber(post.view_count) && !isNumber(post.instagram_details?.story_view_count)) || !isNumber(engagements)) {
        return null;
    }

    const totalViews = (post.view_count ?? 0) + (post.instagram_details?.story_view_count ?? 0);

    if (totalViews === 0 || engagements === 0) {
        return 0;
    }

    return engagements / totalViews;
};

export const getYoutubeTotalEngagement = (post: creatorbase.PublicYoutubePost) => {
    if (
        !post.youtube_details ||
        (!isNumber(post.youtube_details.like_count) && !isNumber(post.youtube_details.comment_count))
    ) {
        return null;
    }

    const { like_count, comment_count } = post.youtube_details;
    return (like_count ?? 0) + (comment_count ?? 0);
};

export const getYoutubeEngagementRatePercentageString = (post: creatorbase.PublicYoutubePost) => {
    const engagements = getYoutubeTotalEngagement(post);

    if (!isNumber(post.view_count) || !isNumber(engagements)) {
        return '-';
    }

    if (post.view_count === 0 || engagements === 0) {
        return '0%';
    }

    return formatDecimalToPercentageString(engagements / post.view_count);
};

export const postStatusLabels: Record<creatorbase.PostStatus, string> = {
    planned: 'Planned',
    live: 'Live',
    backup: 'Backup',
    briefed: 'Briefed',
    draft_approved: 'Draft Approved',
    draft_pending: 'Expecting Draft',
    draft_received: 'Draft Received',
    redrafting: 'Redrafting',
    rejected_or_declined: 'Rejected/Declined',
    to_replace: 'To Replace',
};

export const backupPostsStatuses: creatorbase.BackupPostStatus[] = ['backup', 'rejected_or_declined'];
export const activePostsStatuses: creatorbase.ActivePostStatus[] = [
    'to_replace',
    'briefed',
    'planned',
    'draft_approved',
    'draft_received',
    'draft_pending',
    'redrafting',
    'live',
];

type PostCostFieldsParams = {
    isPostCostEditable: boolean;
    isPaymentEnabled: boolean | undefined;
    campaignCurrencyId: number | null | undefined;
    cost: number | null;
    currencyId: number | null;
};

/**
 * The cost fields to create a post with. A creator on a currency the campaign does not
 * use is added without a cost rather than having one converted or rejected.
 */
export const getPostCostFields = ({
    isPostCostEditable,
    isPaymentEnabled,
    campaignCurrencyId,
    cost,
    currencyId,
}: PostCostFieldsParams) => {
    if (!isPostCostEditable) {
        return undefined;
    }

    if (isPaymentEnabled) {
        return { base_cost: cost, currency_id: currencyId };
    }

    if (currencyId === campaignCurrencyId) {
        return { cost };
    }

    return { cost: null };
};

/**
 * Only tiktok and instagram creators have a stored cost - a youtube creator's rate isn't tracked here.
 */
export const getClientInfluencerPlatformCost = (
    clientInfluencer: creatorbase.ClientInfluencer | null | undefined,
    platform: creatorbase.Platform
): { cost: number | null; currencyId: number | null } => {
    if (platform === 'tiktok') {
        return {
            cost: clientInfluencer?.tiktok_cost ?? null,
            currencyId: clientInfluencer?.tiktok_currency_id ?? null,
        };
    }

    if (platform === 'instagram') {
        return {
            cost: clientInfluencer?.instagram_cost ?? null,
            currencyId: clientInfluencer?.instagram_currency_id ?? null,
        };
    }

    return { cost: null, currencyId: null };
};
